/**
 * SensitiveDataToggle Component
 *
 * Toggle button for the navbar to show/hide sensitive information globally.
 * Features:
 * - Eye/EyeOff icon based on state
 * - Tooltip with current state
 * - Keyboard shortcut support (Ctrl+Shift+H)
 * - Auto-hide on print
 * - Auto-hide on idle (5 minutes)
 * - Cross-tab synchronization
 * - Visibility API detection
 * - Smooth transition animations
 * - Accessible with ARIA attributes
 *
 * @example
 * <SensitiveDataToggle />
 */

import React, { useEffect, useRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { usePrivacyStore } from '@/stores/privacyStore'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const IDLE_TIMEOUT = 5 * 60 * 1000 // 5 minutes
const BROADCAST_CHANNEL_NAME = 'monutrack-privacy-sync'

export const SensitiveDataToggle: React.FC = () => {
  const {
    isSensitiveHidden,
    toggleSensitiveData,
    setSensitiveData,
    autoHideOnIdle,
    autoHideOnPrint,
    updateActivity,
  } = usePrivacyStore()

  const idleTimerRef = useRef<NodeJS.Timeout | null>(null)
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null)
  const previousStateRef = useRef<boolean>(false)

  // Track activity for idle detection
  const handleActivity = () => {
    updateActivity()
    resetIdleTimer()
  }

  // Reset idle timer
  const resetIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
    }

    if (autoHideOnIdle) {
      idleTimerRef.current = setTimeout(() => {
        console.log('🔒 Auto-hiding due to inactivity')
        setSensitiveData(true)
      }, IDLE_TIMEOUT)
    }
  }

  // Keyboard shortcut: Ctrl+Shift+H (or Cmd+Shift+H on Mac)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === 'h'
      ) {
        event.preventDefault()
        toggleSensitiveData()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [toggleSensitiveData])

  // Auto-hide on print
  useEffect(() => {
    if (!autoHideOnPrint) return

    const handleBeforePrint = () => {
      console.log('🖨️ Print detected - auto-hiding sensitive data')
      previousStateRef.current = isSensitiveHidden
      setSensitiveData(true)
    }

    const handleAfterPrint = () => {
      console.log('🖨️ Print complete - restoring previous state')
      // Restore previous state after printing
      setSensitiveData(previousStateRef.current)
    }

    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
    }
  }, [autoHideOnPrint, setSensitiveData, isSensitiveHidden])

  // Idle detection - Track user activity
  useEffect(() => {
    if (!autoHideOnIdle) return

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true)
    })

    // Start initial timer
    resetIdleTimer()

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true)
      })
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
      }
    }
  }, [autoHideOnIdle])

  // Cross-tab synchronization with BroadcastChannel
  useEffect(() => {
    // Check if BroadcastChannel is supported
    if (typeof BroadcastChannel === 'undefined') {
      console.log('⚠️ BroadcastChannel not supported, falling back to localStorage events')

      // Fallback to localStorage events for cross-tab sync
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === 'monutrack-privacy-settings' && e.newValue) {
          try {
            const newState = JSON.parse(e.newValue)
            if (newState.state?.isSensitiveHidden !== isSensitiveHidden) {
              console.log('📡 Privacy state synced from another tab (localStorage)')
            }
          } catch (err) {
            console.error('Error parsing privacy state from localStorage:', err)
          }
        }
      }

      window.addEventListener('storage', handleStorageChange)
      return () => window.removeEventListener('storage', handleStorageChange)
    }

    // Use BroadcastChannel for modern browsers
    try {
      broadcastChannelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME)

      // Listen for messages from other tabs
      broadcastChannelRef.current.onmessage = (event) => {
        if (event.data.type === 'PRIVACY_STATE_CHANGE') {
          console.log('📡 Privacy state synced from another tab:', event.data.hidden)
          setSensitiveData(event.data.hidden)
        }
      }

      return () => {
        broadcastChannelRef.current?.close()
      }
    } catch (err) {
      console.error('Error setting up BroadcastChannel:', err)
    }
  }, [setSensitiveData, isSensitiveHidden])

  // Broadcast state changes to other tabs
  useEffect(() => {
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage({
          type: 'PRIVACY_STATE_CHANGE',
          hidden: isSensitiveHidden,
        })
      } catch (err) {
        console.error('Error broadcasting privacy state:', err)
      }
    }
  }, [isSensitiveHidden])

  // Visibility API - Warn when tab becomes visible with sensitive data shown
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isSensitiveHidden) {
        console.log('👁️ Tab visible with sensitive data shown')
        // Could show a toast notification here if needed
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isSensitiveHidden])

  const tooltipText = isSensitiveHidden
    ? 'Exibir informações'
    : 'Ocultar informações'

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={toggleSensitiveData}
            className={`btn-icon transition-all duration-200 group relative ${
              isSensitiveHidden
                ? 'text-brand-primary hover:text-brand-accent'
                : 'text-gray-400 hover:text-brand-primary'
            }`}
            aria-label="Toggle sensitive information visibility"
            aria-pressed={isSensitiveHidden}
            aria-keyshortcuts="Control+Shift+H"
            role="switch"
          >
            {isSensitiveHidden ? (
              <EyeOff
                size={20}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:scale-110"
              />
            ) : (
              <Eye
                size={20}
                strokeWidth={1.5}
                className="transition-transform duration-300 group-hover:scale-110"
              />
            )}
            {isSensitiveHidden && (
              <span className="absolute -top-1 -right-1 h-2 w-2 bg-brand-primary rounded-full animate-pulse" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
